import type { Difficulty, Move, Rule } from './types';
import { chooseStyledMove, type RapfiCandidate } from './style';

type EngineStatus = 'loading' | 'ready' | 'thinking' | 'error';

export interface EngineUpdate {
  status: EngineStatus;
  message: string;
  progress?: number;
}

const RULE_CODE: Record<Rule, number> = { freestyle: 0, standard: 1, renju: 2 };

function boardCommand(moves: Move[]): string {
  if (!moves.length) return 'YXBOARD DONE';
  return `YXBOARD ${moves.map((move) => `${move.x},${move.y},${move.side}`).join(' ')} DONE`;
}

export class RapfiClient {
  private worker!: Worker;
  private ready = false;
  private configured = false;
  private readyPromise!: Promise<void>;
  private resolveReady!: () => void;
  private rejectReady!: (error: Error) => void;
  private moveResolver: ((move: [number, number]) => void) | null = null;
  private moveRejecter: ((error: Error) => void) | null = null;
  private thinkTimer = 0;
  private thinkContext: { moves: Move[]; rule: Rule; difficulty: Difficulty } | null = null;
  private currentCandidate: Partial<RapfiCandidate> | null = null;
  private readonly candidates = new Map<number, RapfiCandidate>();
  private forbidResolver: ((moves: Set<string>) => void) | null = null;
  private forbidRejecter: ((error: Error) => void) | null = null;
  private forbidTimer = 0;
  private readonly listeners = new Set<(update: EngineUpdate) => void>();

  constructor() {
    this.resetReadyPromise();
    this.startWorker();
  }

  private resetReadyPromise(): void {
    this.readyPromise = new Promise((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
  }

  private startWorker(): void {
    this.worker = new Worker('/engine/bridge.worker.js');
    this.worker.onmessage = ({ data }) => this.handleMessage(data);
    this.worker.onerror = () => {
      this.fail('Rapfi Worker 無法啟動');
      this.restartWorker('正在重新啟動 Rapfi');
    };
  }

  private restartWorker(message: string): void {
    this.worker.terminate();
    window.clearTimeout(this.thinkTimer);
    window.clearTimeout(this.forbidTimer);
    this.moveResolver = null;
    this.moveRejecter = null;
    this.forbidResolver = null;
    this.forbidRejecter = null;
    this.thinkContext = null;
    this.currentCandidate = null;
    this.candidates.clear();
    this.ready = false;
    this.configured = false;
    this.resetReadyPromise();
    this.emit({ status: 'loading', message });
    this.startWorker();
  }

  onUpdate(listener: (update: EngineUpdate) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(update: EngineUpdate): void {
    this.listeners.forEach((listener) => listener(update));
  }

  private fail(message: string): void {
    const error = new Error(message);
    this.emit({ status: 'error', message });
    if (!this.ready) this.rejectReady(error);
    if (this.moveRejecter) this.moveRejecter(error);
    if (this.forbidRejecter) this.forbidRejecter(error);
  }

  private handleMessage(payload: { type: string; data: unknown }): void {
    if (payload.type === 'ready') {
      this.ready = true;
      this.resolveReady();
      this.emit({ status: 'ready', message: 'Rapfi 已就緒' });
      return;
    }
    if (payload.type === 'status') {
      if (this.ready) return;
      const text = String(payload.data ?? '');
      const match = text.match(/\((\d+)\/(\d+)\)/);
      this.emit({ status: 'loading', message: match ? '正在載入 Rapfi 神經網路' : '正在啟動 Rapfi', progress: match ? Number(match[1]) / Number(match[2]) : undefined });
      return;
    }
    if (payload.type === 'error') {
      this.fail(String(payload.data));
      this.restartWorker('正在重新啟動 Rapfi');
      return;
    }
    if (payload.type !== 'stdout') return;
    const line = String(payload.data).trim();
    const pvStart = line.match(/^INFO PV (\d+)$/);
    if (pvStart) {
      this.currentCandidate = { rank: Number(pvStart[1]) };
      return;
    }
    if (line === 'INFO PV DONE') {
      const candidate = this.currentCandidate;
      if (candidate?.move && typeof candidate.rank === 'number') this.candidates.set(candidate.rank, candidate as RapfiCandidate);
      this.currentCandidate = null;
      return;
    }
    if (this.currentCandidate) {
      const bestLine = line.match(/^INFO BESTLINE (\d+),(\d+)/);
      if (bestLine) this.currentCandidate.move = [Number(bestLine[1]), Number(bestLine[2])];
      const numeric = line.match(/^INFO (EVAL|WINRATE|STDEV|LCBVALUE) (-?\d+(?:\.\d+)?)/);
      if (numeric) {
        const key = ({ EVAL: 'evaluation', WINRATE: 'winRate', STDEV: 'stdev', LCBVALUE: 'lcbValue' } as const)[numeric[1] as 'EVAL' | 'WINRATE' | 'STDEV' | 'LCBVALUE'];
        this.currentCandidate[key] = Number(numeric[2]);
      }
      return;
    }
    if (/^\d+,\d+$/.test(line) && this.moveResolver) {
      const [x, y] = line.split(',').map(Number);
      const resolve = this.moveResolver;
      this.moveResolver = null;
      this.moveRejecter = null;
      window.clearTimeout(this.thinkTimer);
      const context = this.thinkContext;
      const move = context
        ? chooseStyledMove(context.moves, context.rule, context.difficulty, [...this.candidates.values()], [x, y])
        : [x, y] as [number, number];
      this.thinkContext = null;
      this.candidates.clear();
      resolve(move);
      this.emit({ status: 'ready', message: 'Rapfi 已就緒' });
    } else if (line.startsWith('FORBID') && this.forbidResolver) {
      const forbidden = new Set<string>();
      for (const match of line.slice(6).matchAll(/(\d{2})(\d{2})/g)) forbidden.add(`${Number(match[1])},${Number(match[2])}`);
      const resolve = this.forbidResolver;
      this.forbidResolver = null;
      this.forbidRejecter = null;
      window.clearTimeout(this.forbidTimer);
      resolve(forbidden);
    } else if (line.startsWith('ERROR')) {
      this.fail(line.slice(5).trim() || 'Rapfi 回報錯誤');
      this.restartWorker('正在重新啟動 Rapfi');
    }
  }

  private command(command: string): void {
    this.worker.postMessage({ type: 'command', command });
  }

  private configure(rule: Rule, difficulty: Difficulty): void {
    this.command('START 15');
    this.command(`INFO RULE ${RULE_CODE[rule]}`);
    this.command(`INFO STRENGTH ${difficulty.strength}`);
    this.command(`INFO TIMEOUT_TURN ${difficulty.thinkTime}`);
    this.command(`INFO TIMEOUT_MATCH ${Math.max(60000, difficulty.thinkTime * 60)}`);
    this.command('INFO MAX_DEPTH 100');
    this.command('INFO MAX_NODE 0');
    if (!this.configured) {
      this.command('INFO THREAD_NUM 1');
      this.command('INFO SHOW_DETAIL 2');
      this.command('INFO PONDERING 0');
      this.command('INFO HASH_SIZE 32768');
      this.command('RELOADCONFIG config.toml');
      this.configured = true;
    }
  }

  async think(moves: Move[], rule: Rule, difficulty: Difficulty): Promise<[number, number]> {
    await this.readyPromise;
    this.configure(rule, difficulty);
    this.command(boardCommand(moves));
    this.emit({ status: 'thinking', message: `${difficulty.name}正在推演` });
    return new Promise((resolve, reject) => {
      this.candidates.clear();
      this.currentCandidate = null;
      this.thinkContext = { moves: [...moves], rule, difficulty };
      this.moveResolver = resolve;
      this.moveRejecter = reject;
      this.command('YXNBEST 10');
      this.thinkTimer = window.setTimeout(() => {
        if (!this.moveResolver) return;
        this.moveResolver = null;
        this.moveRejecter = null;
        reject(new Error('Rapfi 思考逾時'));
        this.restartWorker('Rapfi 思考逾時，正在重新載入');
      }, Math.max(8000, difficulty.thinkTime + 5000));
    });
  }

  async forbiddenMoves(moves: Move[]): Promise<Set<string>> {
    await this.readyPromise;
    const dummyDifficulty: Difficulty = {
      id: 'rules', name: '', gender: 'male', english: '', title: '', description: '', strength: 100, thinkTime: 1000, accent: '',
      image: '', cardImage: '', rank: '', strengthLabel: '', playStyle: '', styleType: 'balanced', styleLabel: '', attack: 100, defense: 100,
    };
    this.configure('renju', dummyDifficulty);
    this.command(boardCommand(moves));
    return new Promise((resolve, reject) => {
      this.forbidResolver = resolve;
      this.forbidRejecter = reject;
      this.command('YXSHOWFORBID');
      this.forbidTimer = window.setTimeout(() => {
        if (!this.forbidResolver) return;
        this.forbidResolver = null;
        this.forbidRejecter = null;
        reject(new Error('連珠禁手判定逾時'));
        this.restartWorker('禁手判定逾時，正在重新載入 Rapfi');
      }, 4500);
    });
  }
}
